import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckSquare, Plus, X, AlertCircle } from 'lucide-react'
import api from '../services/api'

const PRIORITY_BADGE: Record<string, string> = {
  immediate: 'badge-critical', short_term: 'badge-high', medium_term: 'badge-medium', systemic: 'badge-low',
}
const STATUS_BADGE: Record<string, string> = {
  open: 'badge-high', in_progress: 'badge-medium', overdue: 'badge-critical', closed: 'badge-low', verified: 'badge-low',
}

const EMPTY_FORM = {
  title: '', description: '', priority: 'short_term', owner_id: '', due_date: '',
  source_type: '', verification_method: '',
}

export default function CorrectiveActions() {
  const [data, setData] = useState<{ open: any[]; in_progress: any[]; overdue: any[]; closed: any[]; verified: any[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState<any[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  const load = async () => {
    setLoading(true)
    api.get('/corrective-actions/view/kanban').then(r => setData(r.data)).finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => {})
  }, [])

  const totalOpen = (data?.open?.length || 0) + (data?.in_progress?.length || 0) + (data?.overdue?.length || 0)

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim() || !form.description.trim() || !form.owner_id || !form.due_date) {
      setCreateError('Title, description, owner, and due date are required')
      return
    }
    setCreating(true)
    setCreateError('')
    try {
      await api.post('/corrective-actions', form)
      setShowCreate(false)
      setForm({ ...EMPTY_FORM })
      await load()
    } catch (err: any) {
      setCreateError(err.response?.data?.error || 'Failed to create')
    }
    setCreating(false)
  }

  const Column = ({ title, actions, status }: { title: string; actions: any[]; status: string }) => (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{title}</h3>
        <span className={`badge ${STATUS_BADGE[status] || 'badge-medium'}`}>{actions.length}</span>
      </div>
      <div className="space-y-2">
        {actions.map(a => (
          <Link key={a.id} to={`/corrective-actions/${a.id}`} className="block">
            <div className="card p-3 text-sm space-y-1 hover:shadow-md transition-all cursor-pointer">
              <p className="font-medium line-clamp-2" style={{ color: 'var(--text)' }}>{a.title}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{a.action_number}</p>
              <div className="flex items-center gap-2">
                <span className={`badge text-xs ${PRIORITY_BADGE[a.priority] || ''}`}>{a.priority?.replace('_', ' ')}</span>
                {a.due_date && (
                  <span className="text-xs" style={{ color: new Date(a.due_date) < new Date() && a.status !== 'closed' && a.status !== 'verified' ? 'var(--red)' : 'var(--text-muted)' }}>
                    Due {new Date(a.due_date).toLocaleDateString('en-AU')}
                  </span>
                )}
              </div>
              {a.owner_name && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Owner: {a.owner_name}</p>}
            </div>
          </Link>
        ))}
        {actions.length === 0 && <p className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>None</p>}
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Corrective Actions</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{totalOpen} open</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary">
          <Plus size={16} /> New Action
        </button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-2xl rounded-2xl shadow-xl" style={{ background: 'var(--white)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>New Corrective Action</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <form onSubmit={create} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Brief title for this action" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description *</label>
                <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What needs to be done and why?" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Priority *</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                    <option value="immediate">Immediate</option>
                    <option value="short_term">Short Term (7 days)</option>
                    <option value="medium_term">Medium Term (30 days)</option>
                    <option value="systemic">Systemic (90 days)</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Source</label>
                  <select value={form.source_type} onChange={e => setForm(f => ({ ...f, source_type: e.target.value }))}>
                    <option value="">— None —</option>
                    <option value="incident">Incident</option>
                    <option value="audit">Audit</option>
                    <option value="investigation">Investigation</option>
                    <option value="hazard">Hazard</option>
                    <option value="regulatory_change">Regulatory Change</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Owner *</label>
                  <select value={form.owner_id} onChange={e => setForm(f => ({ ...f, owner_id: e.target.value }))} required>
                    <option value="">— Select owner —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Due Date *</label>
                  <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} required />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Verification Method</label>
                <input value={form.verification_method} onChange={e => setForm(f => ({ ...f, verification_method: e.target.value }))} placeholder="How will completion be verified?" />
              </div>

              {createError && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{createError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? 'Creating...' : 'Create Action'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>
      ) : !data ? null : (
        <div className="overflow-x-auto">
          <div className="flex gap-4 min-w-[800px]">
            <Column title="Open" actions={data.open} status="open" />
            <Column title="In Progress" actions={data.in_progress} status="in_progress" />
            <Column title="Overdue" actions={data.overdue} status="overdue" />
            <Column title="Closed" actions={data.closed} status="closed" />
            <Column title="Verified" actions={data.verified} status="verified" />
          </div>
        </div>
      )}
    </div>
  )
}
